import React, {useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import FullScreen from "react-full-screen";
import Link from "@amzn/meridian/link";
import {FormattedMessage} from "react-intl";
import Text from "@amzn/meridian/text"
import Box from "@amzn/meridian/box";
import {
    AVAILABLE, BLOCKED,
    MANUAL_ASSIGNMENT, MANUALLY_PRIORITISED, OPTIMAL, OVERCROWDED,
    PICKLIST_ASSIGNABLE,
    PICKLIST_ASSIGNED,
    PICKLIST_IN_PROGRESS,
    PICKLIST_LOCKED, TIME_CRITICAL
} from "../pickStatus/constants/Constants";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Column from "@amzn/meridian/column";
import ScrollableElementWrapper from "../commonModals/ScrollableElementWrapper";
import {getRefreshInterval} from "../util/Util";
import {CORE_AMZL} from "../util/Constants";
import {getRemainingTTL, isPickStatusInGridCacheValid} from "../util/CacheManager";
import {PICK_STATUS_IN_GRID_CACHE_KEY} from "../util/LocalStorageKeys";
import {PickContext} from "./context/PickContext";
import {PickFilterContext} from "./context/PickFilterContext";
import useCapabilityContext from "../global/context/useCapabilityContext";
import * as FilterUtil from "../util/FilterUtil";

const LayoutGridView = () => {
    const [screenState, setScreenState] = useState(false);
    const {viewModel, dispatch} = useContext(PickContext);
    const {viewModelF} = useContext(PickFilterContext);
    const {configViewModel, configDispatch, configViewModelRef} = useCapabilityContext();

    const toggleScreen = () => {
        setScreenState(!screenState);
    };

    /** Create clone of global available list of picklists in grid per aisle group */
    let picklistsInAisleGroups = Array.from(viewModel.picklistsInAisleGroups);

    /** Create clone of global available list of picklists in grid per work group */
    let picklistsInWorkGroups = viewModel.picklistsInWorkGroups || [];

    /**
     * This gets invoked on following events:
     *  1. On every page load,
     *  2. Every time xpt filter value is changed,
     *  3. When station config's initialised attribute changes - usually happens only once for non ARv2 stations.
     *  Note: Updates on station config does not re-render useEffect, if initialised attribute value is not changed.
     */
    useEffect(() => {
        if (configViewModel.stationConfig.initialised) {
            getPickStatusInGridData();
            let firstPollTime = getRefreshInterval(configViewModelRef.current.stationConfig);
            if (configViewModel.capability === CORE_AMZL) {
                const remainingTTL = getRemainingTTL(PICK_STATUS_IN_GRID_CACHE_KEY);
                if (viewModelF.xptNodes && isPickStatusInGridCacheValid(viewModelF.xptNodes)) {
                    firstPollTime = remainingTTL;
                }
            }

            const timeoutHandler = () => {
                getPickStatusInGridData();
                timeout = setTimeout(timeoutHandler, getRefreshInterval(configViewModelRef.current.stationConfig));
            };
            let timeout = setTimeout(timeoutHandler, firstPollTime);

            return () => clearTimeout(timeout);
        }
    }, [viewModelF.xptNodes, configViewModel.stationConfig.initialised]);

    const getPickStatusInGridData = () => {
        if (configViewModel.capability === CORE_AMZL  || (configViewModel.stationConfig.initialised && Array.isArray(viewModelF.xptNodes))) {
            dispatch({
                type: 'LOAD_PICK_STATUS_IN_GRID',
                data: {
                    dispatcher: dispatch,
                    baseUrl: configViewModel.baseUrl,
                    capability: configViewModel.capability,
                    xptNodes: viewModelF.xptNodes,
                    stationConfig: configViewModel.stationConfig,
                    configDispatch: configDispatch
                }
            });
        }
    };

    /** Apply filters on global available list of piklists and extract layout and filtered list of picklists per aisle group */
    let filteredLayoutGridViewData = FilterUtil.applyGlobalPickStatusInGridFilters(
        picklistsInAisleGroups, picklistsInWorkGroups, viewModelF, configViewModel);

    const boxWidth = 22;
    const boxHeight = 22;

    const getSquareBox = (color, height, width, blockedPicklist) => {
        if (blockedPicklist) {
            return getBlockedPicklistIcon(height, width)
        }
        return <Box minHeight={height} minWidth={width}
                    maxHeight={height} maxWidth={width}
                    backgroundColor={color}>
        </Box>
    };

    const getSquareBoxWithBoundary = (color, blockedPicklist) => {
        return <Box minHeight={boxHeight} minWidth={boxWidth}
                    maxHeight={boxHeight} maxWidth={boxWidth}
                    backgroundColor="#AD0A30" spacingInset="100">
            {getSquareBox(color, boxHeight-4, boxWidth-4, blockedPicklist)}
        </Box>
    };

    const blockedPicklistImageSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAACOklEQVR4Ae3YSy5EURSF4askJdGQIEakJSZlUqJDwoBUHw2yEJSq+ziPfc5+rK+7e//q7YP7x6f3gUQ9bzbD7d3D8PL6tnNbDSRqKj5wAEFz8YEDCFkSHziAgKXxgQNUlhL/aL3mADWlxr++uuQAteTEPz875QA15MYHDlCoJD5wgAKl8YEDZKoRHzhAhlrxgQMkqhkfOECC2vGBAywkER84wAJS8YEDzJCMDxxggnR84AAjWsQHDrBHq/jAAf5pGR84wB+t4wMH+NYjPnCAoV98CD9Az/gQeoDe8SHsABriQ8gBtMSHcANoig+hBtAWH8IMoDE+hBhAa3xwP4Dm+OB6AO3xwe0AFuKDywGsxAd3A1iKD64GsBYf3AxgMT64GMBqfDA/gOX4YHoA6/HB7AAe4oPJAbzEB3MDeIoPpgbwFh/MDOAxPpgYwGt8UD+A5/igegDv8UHtABHig8oBosQHdQNEig+qBogWH9QMEDE+qBgganzoPkDk+NB1gOjxodsAjP+lywCM/6v5AIy/rekAjL+r2QCMv1+TARh/nPgAjD9NdADGnyc2AOMvIzIA4y9XfQDGT1N1AMZPV20Axs9TZQDGz1c8AOOXKRqA8ctlD8D4dWQNwPj1JA/A+HUlDcD49S0egPFlLBqA8eXMDsD4siYHYHx5owMwfht7B2D8dnYGYPy2tgZg/PZ+BmD8Pj4HYPx+Vozf1+HxycUN4/ezYvy+Zl8RjC9rcgDGlzc6AOO3sXcAxm/nA2y1NJAEJMr2AAAAAElFTkSuQmCC";
    const getBlockedPicklistIcon = (height, width) => {
        return <img alt={"Blocked Picklist"}
                    src={blockedPicklistImageSrc}
                    width={width} height={height}/>
    };


    const getPicklistColor = (status, assignReason, isDwelling) => {
        switch (status) {
            case PICKLIST_ASSIGNABLE:
                return isDwelling ? "#9370DB" : "#BBC0C1";
            case PICKLIST_ASSIGNED:
            case PICKLIST_IN_PROGRESS:
            case PICKLIST_LOCKED:
                switch (assignReason) {
                    case MANUAL_ASSIGNMENT:
                        return "#47C2EC";
                    case MANUALLY_PRIORITISED:
                        return "#FAD000";
                    case OPTIMAL:
                    case TIME_CRITICAL:
                        return "#98C91E";
                    default:
                        return "#BBC0C1";
                }
            default:
                return "#BBC0C1";
        }
    };

    const getPicklistStatus = (status, workAreaStatus, assignReason, isDwelling) => {
        switch (status) {
            case PICKLIST_ASSIGNABLE:
                return isDwelling
                    ? "Cart is dwelling in the output queue"
                    : (workAreaStatus === AVAILABLE ? "Can be assigned" : "Assignment blocked");
            case PICKLIST_ASSIGNED:
            case PICKLIST_IN_PROGRESS:
            case PICKLIST_LOCKED:
                switch (assignReason) {
                    case MANUAL_ASSIGNMENT:
                        return "Manually assigned";
                    case MANUALLY_PRIORITISED:
                        return "Manually prioritised";
                    case OPTIMAL:
                    case TIME_CRITICAL:
                        return "Optimal auto-assignment";
                    default:
                        return "Non-optimal assignment";
                }
            default:
                return "Not ready";
        }
    };

    const getAisleGroupColor = (aisleGroupStatus) => {
        if (aisleGroupStatus === OVERCROWDED) {
            return "#DE9DAC";
        } else {
            return "primary";
        }
    };

    const renderPicklistBox = (picklist, workAreaStatus) => {
        // Determine if the picklist is blocked based on its status and aisle group status
        const isBlocked = (picklist.status === PICKLIST_ASSIGNABLE &&
            (workAreaStatus === BLOCKED || workAreaStatus === OVERCROWDED));

        // Get the picklist color based on its properties
        const picklistColor = getPicklistColor(
            picklist.status,
            picklist.assignmentReason,
            picklist.isDwelling
        );

        const boxContent = picklist.timeCritical
            ? getSquareBoxWithBoundary(picklistColor, isBlocked)
            : getSquareBox(picklistColor, boxWidth, boxHeight, isBlocked);

        return (
            <div className="html-tooltip">
                <Row spacing="none" spacingInset="none">
                    {getToolTipBody(picklist, workAreaStatus)}
                    {boxContent}
                </Row>
            </div>
        );
    };

    const getToolTipBody = (picklist, workAreaStatus) => {
        return <Column className="tooltip-text" spacing={"none"}>
            <Row alignmentHorizontal={"center"}><b>{picklist.picklistCode}</b><br/></Row>
            <Column spacing={"none"} alignmentHorizontal={"left"}>
                <span>Status: <b>{getPicklistStatus(picklist.status, workAreaStatus, picklist.assignmentReason, picklist.isDwelling)}</b><br/></span>
                <span>Time critical: <b>{picklist.timeCritical ? "true" : "false"}</b><br/></span>
                {picklist.waitTimeTillAtRisk > 0 && <span>Pending time before critical: <b>
                    {new Date(picklist.waitTimeTillAtRisk).getMinutes() + " min"}</b><br/></span>}
                <span>Dispatch time: <b>{new Date(picklist.dispatchTime).toLocaleTimeString()}</b><br/></span>
            </Column>
        </Column>
    };

    function sortAisleGroupsForCluster(a, b) {
        return a.aisles[0].localeCompare(b.aisles[0], 'en', { numeric: true })
    }

    const generatePicklistsInWorkGroups = (workGroupToPicklistMapping) => {
        const { picklists, workGroup } = workGroupToPicklistMapping;

        if (!picklists || !picklists.length) {
            return null;
        }

        // The work area status for work groups will default to "available"
        // since we've only retrieved the assigned work group labels.
        const workGroupComponents = [];
        const picklistColumn = (
            <div key={String.fromCharCode(65)} className="col">
                <div className="rack">
                    <Row spacing="small" spacingInset="small" wrap="down">
                        {picklists.map((picklist) => renderPicklistBox(picklist, AVAILABLE))}
                    </Row>
                </div>
            </div>
        );

        workGroupComponents.push(
            <div key={workGroup.sourceLocationLabel} className="aisle">
                <Row minHeight="50px" height="auto" backgroundColor="primary">
                    <span
                        style={{
                            paddingLeft: "4px",
                            marginRight: "10px",
                            width: "110px",
                            fontWeight: "bold",
                        }}
                    >
                      {workGroup.sourceLocationLabel}
                    </span>
                </Row>
                {picklistColumn}
            </div>
        );

        return workGroupComponents;
    }

    // Generate aisle groups for each cluster and picklists in each aisle group
    const generatePicklistsInAisleGroups = (aisleGroups, aisleGroupToPicklistMapping) => {
        if (Array.isArray(aisleGroups) && aisleGroups.length > 0) {
            const sortedAisleGroupData = aisleGroups.sort(sortAisleGroupsForCluster);
            let aisleGroupComponents = [];

            sortedAisleGroupData.forEach(aisleGroup => {
                let aisleLabels = null;
                if (Array.isArray(aisleGroup.aisles) && aisleGroup.aisles.length > 0) {
                    aisleLabels = aisleGroup.aisles[0];
                    if (aisleGroup.aisles.length === 2) {
                        aisleLabels = aisleLabels.concat(" , ").concat(aisleGroup.aisles[1])
                    }
                }
                const aisleGroupData = aisleGroupToPicklistMapping[aisleGroup.aisles];
                let picklists = aisleGroupData ? aisleGroupData.picklists : [];
                let aisleGroupStatus = aisleGroupData ? aisleGroupData.aisleGroupStatus : "";

                const aisleGroupColumn = [];
                let picklistComponentsPerAisleGroup = "";
                if (Array.isArray(picklists) && picklists.length > 0) {
                    picklistComponentsPerAisleGroup = <Row spacing={"small"} spacingInset={"small"} wrap={"down"}>
                        {picklists.map((picklist) => renderPicklistBox(picklist, aisleGroupStatus))}
                    </Row>;
                }
                aisleGroupColumn.push(<div key={String.fromCharCode(65)} className="col">
                    <div className="rack">
                        {picklistComponentsPerAisleGroup}
                    </div>
                </div>);

                aisleGroupComponents.push(<div key={aisleLabels} className="aisle">
                    <Row minHeight={"50px"} height={"auto"}
                         backgroundColor={getAisleGroupColor(aisleGroupStatus)}>
                    <span style={{paddingLeft: "4px", marginRight: '10px', width: '110px', fontWeight: 'bold'}}>
                        {aisleLabels}
                    </span></Row>{aisleGroupColumn}</div>);
            });
            return aisleGroupComponents;
        }
        return null;
    };

    // Generate clusters for layout grid
    const generateLayoutGrid = () => {
        const aisleGroupToPicklistMapping = filteredLayoutGridViewData.aisleGroupToPicklistMapping;
        const workGroupToPicklistMapping = filteredLayoutGridViewData.workGroupToPicklistMapping;
        const clusterLabels = filteredLayoutGridViewData.clusters;
        const layoutGridElements = [];

        clusterLabels.forEach(cluster => {
            const clusterLabel = "Cluster " + cluster;
            const aisles = generatePicklistsInAisleGroups(filteredLayoutGridViewData.clusterToAisleGroupMapping.get(cluster), aisleGroupToPicklistMapping);
            if (aisles) {
                layoutGridElements.push(<div key={clusterLabel} className="cluster">
                    <span style={{paddingBottom: "10px", fontWeight: 'bold'}}>{clusterLabel}</span>{aisles}</div>);
            }
        });

        if (configViewModel.stationConfig.multiStationPicklistViewEnabled && workGroupToPicklistMapping) {
            let picklistBoxes = []
            workGroupToPicklistMapping.forEach(info => {
                picklistBoxes = picklistBoxes.concat(generatePicklistsInWorkGroups(info));
            });
            if (picklistBoxes && picklistBoxes.length > 0) {
                layoutGridElements.push(<div key="workStations" className="cluster">
                    <span style={{paddingBottom: "10px", fontWeight: 'bold'}}>Work Stations</span>{picklistBoxes}</div>);
            }
        }

        return layoutGridElements;
    };

    const getDefaultLegend = (color, labelId, defaultLabel, timeCritical, blockedPicklist) => {
        return <Row wrap={"none"} spacing={"small"}>
            {timeCritical ? getSquareBoxWithBoundary(color, blockedPicklist) :
                getSquareBox(color, boxHeight, boxWidth, blockedPicklist)}
            <Text>
                <FormattedMessage id={labelId} defaultMessage={defaultLabel}/>
            </Text>
        </Row>
    };

    return (
        <div>
            <Row widths={["grid-12"]} alignmentVertical="top" spacingInset="medium none medium none" wrap={"down"}>
                <div>
                    <FullScreen enabled={screenState} onChange={(enabled => setScreenState(enabled))}>
                        <ScrollableElementWrapper>
                            <Table headerColumns={2} stickyHeaderColumn={true} headerRows={2} spacing={"small"} fixHeaderRows={true} showDividers={false}>
                                <TableRow>
                                    <TableCell alignmentHorizontal={"left"}>
                                        <Row height={"30px"} spacing="xlarge" alignmentHorizontal={"left"}>
                                            <Row spacing={"none small"}>
                                                <Column width={"80px"}>
                                                    <FormattedMessage id="outboundAMZL.aislesLegend" defaultMessage="Aisles :"/>
                                                </Column>
                                                {getDefaultLegend(getAisleGroupColor(OVERCROWDED), "outboundAMZL.overcrowdedAisleLabel", "Overcrowded Aisles")}
                                            </Row>
                                        </Row>
                                        <Row height={"30px"} spacing="xlarge" alignmentHorizontal={"left"}>
                                            <Row spacing={"none small"}>
                                                <Column width={"80px"}>
                                                    <FormattedMessage id="outboundAMZL.picklistsLegend" defaultMessage="Picklists :"/>
                                                </Column>
                                                {getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNABLE), "outboundAMZL.availablePicklist", "Available For Assignment")}
                                                {getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNABLE), "outboundAMZL.blockedPicklist", "Assignment Blocked", false, true)}
                                                {configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                                                    getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNABLE, OPTIMAL, true), "outboundAMZL.DwellingPicklist", "Dwelling")}
                                                {getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNED, MANUAL_ASSIGNMENT), "outboundAMZL.manuallyAssignedPicklist", "Manually Assigned")}
                                                {getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNED, MANUALLY_PRIORITISED), "outboundAMZL.manuallyPrioritisedPicklist", "Manually Prioritised")}
                                                {getDefaultLegend(getPicklistColor(PICKLIST_ASSIGNED, OPTIMAL), "outboundAMZL.optimallyAssignedPicklist", "Assigned Optimally")}
                                                {getDefaultLegend("primary", "outboundAMZL.timeCriticalPicklist", "Time Critical", true, false)}
                                            </Row>
                                        </Row>
                                    </TableCell>
                                    <TableCell alignmentHorizontal={"right"}>
                                        <Row alignmentHorizontal={"right"} spacing={"none"}>
                                            <Link onClick={toggleScreen} type="secondary">
                                                <FormattedMessage id="outboundAMZL.toggleFullscreen" defaultMessage="Toggle fullscreen"/>
                                            </Link>
                                        </Row>
                                    </TableCell>
                                </TableRow>
                            </Table>
                            <div className="delivery-station">
                                {generateLayoutGrid()}
                            </div>
                        </ScrollableElementWrapper>
                    </FullScreen>
                </div>
            </Row>
        </div>
    );
};

export default LayoutGridView



// WEBPACK FOOTER //
// ./src/components/pickStatus/LayoutGridView.js